import { create } from 'zustand';
import { hasPermission, type PermissionKey } from '../config/permissions';
import { RUNTIME } from '../config/runtime';
import { apiRequest } from '../services/api';
import type { User } from '../types';

interface CloudUser {
  id: string;
  email: string;
  name_ar?: string;
  name_en?: string;
  role: User['role'];
  is_active?: number | boolean;
  last_login_at?: string;
  departments?: string[];
}

interface AuthState {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  hydrate: () => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: PermissionKey) => boolean;
}

function mapCloudUser(user: CloudUser): User {
  const now = new Date().toISOString();
  return {
    id: user.id,
    email: user.email,
    nameAr: user.name_ar || user.name_en || user.email,
    nameEn: user.name_en || user.name_ar || user.email,
    role: user.role,
    departments: user.departments ?? [],
    createdAt: now,
    lastLoginAt: user.last_login_at,
    isActive: user.is_active !== 0 && user.is_active !== false,
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  initialized: false,
  error: null,

  hydrate: async () => {
    if (!RUNTIME.isCloudConfigured) {
      set({ user: null, loading: false, initialized: true, error: null });
      return;
    }
    set({ loading: true, error: null });
    try {
      const response = await apiRequest<{ user: CloudUser }>('/api/auth/me');
      set({ user: mapCloudUser(response.user), loading: false, initialized: true });
    } catch {
      set({ user: null, loading: false, initialized: true });
    }
  },

  logout: async () => {
    if (RUNTIME.isCloudConfigured) await apiRequest('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    set({ user: null, error: null, initialized: true });
  },

  can: permission => {
    const { user } = get();
    return user ? hasPermission(user.role, permission) : false;
  },
}));
